import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { ModalConfirmOkDialog } from '../components/ModalConfirmOkDialog'
import { loadStatus } from '../constants/loadStatus'
import { confirmMsg, errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC103PasswordUpdate } from '../hooks/useC103PasswordUpdate'
import { softPasswordSchema, strictPasswordSchema } from '../schema/loginSchema'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'

// パスワード変更画面
export const Password = () => {
  // hooks
  const auth = useAuth()
  const c103 = useC103PasswordUpdate()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // ダイアログ
  const [open, setOpen] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)

  const [errorCurrentPassword, setErrorCurrentPassword] = useState('')
  const [errorNewPassword, setErrorNewPassword] = useState('')
  const [errorConfirmPassword, setErrorConfirmPassword] = useState('')

  const validateCurrentPassword = async (value: string) => {
    try {
      await softPasswordSchema.validate(value)
      setErrorCurrentPassword('')
    } catch (err: any) {
      setErrorCurrentPassword(err.message)
    }
  }
  const validateNewPassword = async (value: string) => {
    try {
      await strictPasswordSchema.validate(value)
      if (confirmPassword !== '') {
        if (confirmPassword === value) {
          if (errorConfirmPassword === errorMsg.PASSWORD_UNMATCH_ERROR) {
            setErrorConfirmPassword('')
          }
          setErrorNewPassword('')
        } else {
          setErrorNewPassword(errorMsg.PASSWORD_UNMATCH_ERROR)
        }
      } else {
        setErrorNewPassword('')
      }
    } catch (err: any) {
      setErrorNewPassword(err.message)
    }
  }
  const validateConfirmPassword = async (value: string) => {
    try {
      await strictPasswordSchema.validate(value)
      if (newPassword === value) {
        if (errorNewPassword === errorMsg.PASSWORD_UNMATCH_ERROR) {
          setErrorNewPassword('')
        }
        setErrorConfirmPassword('')
      } else {
        setErrorConfirmPassword(errorMsg.PASSWORD_UNMATCH_ERROR)
      }
    } catch (err: any) {
      setErrorConfirmPassword(err.message)
    }
  }
  const handleCurrentPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPassword(event.target.value)
    validateCurrentPassword(event.target.value)
  }

  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(event.target.value)
    validateNewPassword(event.target.value)
  }

  const handleConfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(event.target.value)
    validateConfirmPassword(event.target.value)
  }

  const handleToggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(prev => !prev)
  }

  const handleToggleNewPasswordVisibility = () => {
    setShowNewPassword(prev => !prev)
  }

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev)
  }

  const onClickChange = async () => {
    const loginId = auth.loginId
    //  パスワード更新
    const resultPass = await c103.execPasswordUpdate(loginId, currentPassword, newPassword)
    if (resultPass.success) {
      // console.log("execPasswordUpdate successed.");
      // 新パスワードで IDトークン取得
      const result = await auth.signIn(loginId, newPassword)
      if (result.success) {
        // 入力値をリセット
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        // ダイアログオープン
        setOpen(true)
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(resultPass.message!)
      setOpenDialog(true)
    }
  }

  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
    setOpen(false)
  }
  // 入力チェック
  useEffect(() => {
    if (
      currentPassword !== '' &&
      newPassword !== '' &&
      confirmPassword !== '' &&
      errorCurrentPassword === '' &&
      errorNewPassword === '' &&
      errorConfirmPassword === '' &&
      newPassword === confirmPassword
    ) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [
    currentPassword,
    newPassword,
    confirmPassword,
    errorCurrentPassword,
    errorNewPassword,
    errorConfirmPassword,
  ])

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let passwordChangeButtonJsx: JSX.Element
  switch (baseLanguage) {
    case 'it':
      passwordChangeButtonJsx = (
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{
            width: '220px',
            height: '49px',
            borderRadius: '10px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 21px/29px Noto Sans CJK JP',
            letterSpacing: '0px',
            color: '#FFFFFF',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          disabled={isDisabled}
          style={{
            backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
          }}
          onClick={onClickChange}
        >
          {translation['button_change']}
        </Button>
      )
      break
    default:
      passwordChangeButtonJsx = (
        <Button
          type="submit"
          color="primary"
          variant="contained"
          sx={{
            width: '181px',
            height: '49px',
            borderRadius: '10px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 21px/29px Noto Sans CJK JP',
            letterSpacing: '0px',
            color: '#FFFFFF',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          disabled={isDisabled}
          style={{
            backgroundColor: isDisabled ? '#F4F4F4' : '#282828',
          }}
          onClick={onClickChange}
        >
          {translation['button_change']}
        </Button>
      )
  }

  return (
    <>
      {open && (
        <ModalConfirmOkDialog
          open={open}
          text={confirmMsg.PASSWORD_UPDATEED}
          onClick={() => handleDialogClose()}
        />
      )}
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
        }}
      >
        {(c103.loading === loadStatus.LOADING || auth.loading === loadStatus.LOADING) && (
          <CircularProgress />
        )}
      </Box>
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'auto',
            width: '100%',
            marginTop: '80px',
          }}
        >
          <Paper
            elevation={5}
            sx={{
              height: '762px',
              width: '787px',
              boxShadow: '0px 2px 2px #00000026',
              borderRadius: '10px',
            }}
          >
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
              <Stack direction="column">
                <Typography
                  sx={{
                    mt: '140px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  {translation['label_current_password']}
                </Typography>
                <TextField
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  error={!!errorCurrentPassword}
                  helperText={errorCurrentPassword ? translation[errorCurrentPassword] : ' '}
                  onChange={handleCurrentPasswordChange}
                  InputProps={{
                    endAdornment: currentPassword ? (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleCurrentPasswordVisibility}>
                          {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    sx: { background: '#F4F4F4' },
                  }}
                  sx={{
                    mt: '5px',
                    width: '402px',
                    borderRadius: '10px',
                    'input::-ms-reveal': {
                      display: 'none' /* -ms-revealアイコンを非表示にする */,
                    },
                    '& .MuiFormHelperText-root': {
                      whiteSpace: 'pre-line',
                    },
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: errorCurrentPassword ? 'red' : '#282828',
                      },
                    },
                  }}
                  variant="outlined"
                />
                <Typography
                  sx={{
                    mt: '32px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  {translation['label_new_password']}
                </Typography>
                <TextField
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  error={!!errorNewPassword}
                  helperText={errorNewPassword ? translation[errorNewPassword] : ' '}
                  onChange={handleNewPasswordChange}
                  InputProps={{
                    endAdornment: newPassword ? (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleNewPasswordVisibility}>
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    sx: { background: '#F4F4F4' },
                  }}
                  sx={{
                    mt: '5px',
                    width: '402px',
                    borderRadius: '10px',
                    'input::-ms-reveal': {
                      display: 'none' /* -ms-revealアイコンを非表示にする */,
                    },
                    '& .MuiFormHelperText-root': {
                      whiteSpace: 'pre-line',
                    },
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: errorNewPassword ? 'red' : '#282828',
                      },
                    },
                  }}
                  variant="outlined"
                />
                <Typography
                  sx={{
                    mt: '32px',
                    top: '315px',
                    left: '759px',
                    font: 'normal normal normal 21px/29px Noto Sans JP',
                  }}
                >
                  {translation['label_confirm_new_password']}
                </Typography>
                <TextField
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  error={!!errorConfirmPassword}
                  helperText={errorConfirmPassword ? translation[errorConfirmPassword] : ' '}
                  onChange={handleConfirmPasswordChange}
                  InputProps={{
                    endAdornment: confirmPassword ? (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleConfirmPasswordVisibility}>
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                    sx: { background: '#F4F4F4' },
                  }}
                  sx={{
                    mt: '5px',
                    width: '402px',
                    borderRadius: '10px',
                    'input::-ms-reveal': {
                      display: 'none' /* -ms-revealアイコンを非表示にする */,
                    },
                    '& .MuiFormHelperText-root': {
                      whiteSpace: 'pre-line',
                    },
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: errorConfirmPassword ? 'red' : '#282828',
                      },
                    },
                  }}
                  variant="outlined"
                />
              </Stack>
              {/* <Box mt={10}>
                <Typography
                  sx={{
                    width: "380px",
                    height: "24px",
                    textAlign: "center",
                    letterSpacing: "0px",
                    color: "#282828",
                  }}
                >
                  { 対象外? sakao  }
                  パスワードを忘れた方は
                  <Link
                    href=""
                    color="inherit"
                    sx={{ font: "normal normal bold 16px/23px Noto Sans JP" }}
                  >
                    こちら
                  </Link>
                </Typography>
              </Box>*/}
              <Box mt={10}>{passwordChangeButtonJsx}</Box>
            </Box>
          </Paper>
        </Box>
      </Box>
      <Footer />
    </>
  )
}
